/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
} from 'components';
import { useIntl } from 'react-intl';

export default function RateVariation({ title, data, year, lastYear }) {
  const intl = useIntl();
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (data) {
      /**
       * 📅 Definimos los nombres de los meses traducidos usando `intl.formatMessage`
       */
      const months = [
        intl.formatMessage({ id: 'app.months.jan' }),
        intl.formatMessage({ id: 'app.months.feb' }),
        intl.formatMessage({ id: 'app.months.mar' }),
        intl.formatMessage({ id: 'app.months.apr' }),
        intl.formatMessage({ id: 'app.months.may' }),
        intl.formatMessage({ id: 'app.months.jun' }),
        intl.formatMessage({ id: 'app.months.jul' }),
        intl.formatMessage({ id: 'app.months.ago' }),
        intl.formatMessage({ id: 'app.months.sep' }),
        intl.formatMessage({ id: 'app.months.oct' }),
        intl.formatMessage({ id: 'app.months.nov' }),
        intl.formatMessage({ id: 'app.months.dec' }),
      ];

      // 📌 Extraemos los datos de los años actual y anterior
      const currentYearData = data[year]?.GDMTH || {};
      const lastYearData = data[lastYear]?.GDMTH || {};

      /**
       * 🔢 Generamos las claves de los meses en formato "YYYYMM"
       */
      const monthKeys = Array.from({ length: 12 }, (_, i) => {
        const monthNum = (i + 1).toString().padStart(2, '0'); // "01", "02", ..., "12"
        return `${year}${monthNum}`;
      });

      const lastYearMonthKeys = Array.from({ length: 12 }, (_, i) => {
        const monthNum = (i + 1).toString().padStart(2, '0');
        return `${lastYear}${monthNum}`;
      });

      /**
       * 📊 Extraemos los valores asegurando conversión a número
       */
      const basact = monthKeys.map((key) =>
        parseFloat(currentYearData[key]?.b || 0)
      );
      const basant = lastYearMonthKeys.map((key) =>
        parseFloat(lastYearData[key]?.b || 0)
      );
      const intact = monthKeys.map((key) =>
        parseFloat(currentYearData[key]?.i || 0)
      );
      const intant = lastYearMonthKeys.map((key) =>
        parseFloat(lastYearData[key]?.i || 0)
      );
      const punact = monthKeys.map((key) =>
        parseFloat(currentYearData[key]?.p || 0)
      );
      const punant = lastYearMonthKeys.map((key) =>
        parseFloat(lastYearData[key]?.p || 0)
      );

      // 📌 Guardamos los datos formateados en el estado
      setChartData({ months, basact, basant, intact, intant, punact, punant });
    }
  }, [data, year, lastYear, intl]);

  return (
    <Portlet>
      {/* 🏷️ Encabezado con el título */}
      <PortletHeader noDivider={true}>
        {title ? (
          <PortletLabel title={title} />
        ) : (
          <Skeleton variant="text" width={200} height={50} />
        )}
      </PortletHeader>

      <PortletContent>
        <Box height={400} position="relative">
          {chartData ? (
            <Box>
              {/* 📊 Gráfico de barras con los datos de variación de tarifas */}
              <BarChart
                xAxis={[{ scaleType: 'band', data: chartData.months }]}
                series={[
                  {
                    data: chartData.basact,
                    label: intl.formatMessage({
                      id: 'app.meter.board.ratesVariation.dataset.labels.baseCurrYear',
                    }),
                    color: '#00AFEF', // 🔵 Año actual (Base)
                  },
                  {
                    data: chartData.basant,
                    label: intl.formatMessage({
                      id: 'app.meter.board.ratesVariation.dataset.labels.basePrevYear',
                    }),
                    color: '#3A5F0B', // 🟢 Año anterior (Base)
                  },
                  {
                    data: chartData.intact,
                    label: intl.formatMessage({
                      id: 'app.meter.board.ratesVariation.dataset.labels.intermedCurrYear',
                    }),
                    color: '#ebc057', // 🟡 Año actual (Intermedia)
                  },
                  {
                    data: chartData.intant,
                    label: intl.formatMessage({
                      id: 'app.meter.board.ratesVariation.dataset.labels.intermedPrevYear',
                    }),
                    color: '#FFCC00', // 🟠 Año anterior (Intermedia)
                  },
                  {
                    data: chartData.punact,
                    label: intl.formatMessage({
                      id: 'app.meter.board.ratesVariation.dataset.labels.tipCurrYear',
                    }),
                    color: '#ED685E', // 🔴 Año actual (Punta)
                  },
                  {
                    data: chartData.punant,
                    label: intl.formatMessage({
                      id: 'app.meter.board.ratesVariation.dataset.labels.tipPrevYear',
                    }),
                    color: 'red', // 🔴 Año anterior (Punta)
                  },
                ]}
                borderRadius={4} // 🎨 Bordes redondeados en las barras
                height={400} // 📏 Altura del gráfico
              />
            </Box>
          ) : (
            /**
             * ⏳ Mostramos un `Skeleton` si los datos aún no están listos
             */
            <Skeleton variant="rectangular" height={400} width="100%" />
          )}
        </Box>
      </PortletContent>
    </Portlet>
  );
}
